import { type Event, EventTypeEnum, ParticipantTypeEnum } from '@clubspark-node/clubspark-tennis-types';

import { type LevelConfiguration } from 'atoms/tournament';
import {
  RatingTypeEnum,
  ScaleTypeEnum,
} from 'components/panels/eventsSplitDraws/selectedEvent/rankingsAndRatings/types';
import { type ScaleItem } from 'typedefs/store/tmxTypes';

import {
  type ParticipantScaling,
  type ScalingOptions,
  type ScalingPurpose,
  type ScalingTypeOptions,
  type SubScalingType,
} from '.';
import Scaling from './Scaling';

const scaleName = RatingTypeEnum.PT;
const scaleType = ScaleTypeEnum.RATING;

export type BallColour = 'Green' | 'Orange';

export interface ScaleValue {
  ballColour: BallColour;
  totalPoints: number;
  playPoints: number;
  singlesWinPoints: number;
  doublesWinPoints: number;
}

export interface PlayTrackerScaleItem extends ScaleItem {
  eventType: EventTypeEnum;
  scaleDate: string;
  scaleName: RatingTypeEnum;
  scaleType: ScaleTypeEnum;
  scaleValue: ScaleValue;
}

export const getRatingDisplay = (scaleItem: PlayTrackerScaleItem): number => scaleItem?.scaleValue?.totalPoints;

export const getRatingValue = (scaleItem: PlayTrackerScaleItem, ptValueType: string): number => {
  const ballColour = scaleItem?.scaleValue?.ballColour;
  const totalPoints = scaleItem?.scaleValue?.totalPoints;
  const playPoints = scaleItem?.scaleValue?.playPoints;
  const singlesWinPoints = scaleItem?.scaleValue?.singlesWinPoints;
  const doublesWinPoints = scaleItem?.scaleValue?.doublesWinPoints;

  switch (ptValueType) {
    case 'PT Orange play':
      if (ballColour === 'Orange') return Number.isFinite(totalPoints) ? totalPoints : 0;
      return 0;
    case 'PT Green win':
      if (ballColour === 'Green')
        return Number.isFinite(singlesWinPoints) && Number.isFinite(doublesWinPoints)
          ? singlesWinPoints + doublesWinPoints
          : 0;
      return 0;
    case 'PT Green play':
      if (ballColour === 'Green') return Number.isFinite(playPoints) ? playPoints : 0;
      return 0;
  }
};

class PlayTrackerScaling extends Scaling {
  static scaleName = scaleName;
  static scaleType = scaleType;

  public static scaleSortMethod = (
    purpose: ScalingPurpose,
    a: ParticipantScaling<PlayTrackerScaleItem>,
    b: ParticipantScaling<PlayTrackerScaleItem>,
    options: ScalingTypeOptions,
  ): number => this.getValue(b, options) - this.getValue(a, options);

  public static getDisplay = (
    participantScaling: ParticipantScaling<PlayTrackerScaleItem>,
    options?: ScalingTypeOptions,
  ): string => this.getText(participantScaling, options);

  public static getText(
    participantScaling: ParticipantScaling<PlayTrackerScaleItem>,
    options?: ScalingTypeOptions,
  ): string {
    const isDoubles = participantScaling?.participant?.participantType === ParticipantTypeEnum.PAIR;
    const totalValue = this.getValue(participantScaling, options);
    const values = participantScaling.scaleItems.map((scaleItem) => getRatingValue(scaleItem, options?.ptValueType));

    return !isDoubles
      ? totalValue && Number.isFinite(totalValue)
        ? totalValue.toString()
        : '-'
      : `${totalValue ? totalValue.toString() : '-'} (${
          values[0] && Number.isFinite(values[0]) ? values[0].toString() : '-'
        } / ${values[1] && Number.isFinite(values[1]) ? values[1]?.toString() : '-'})`;
  }

  public static getValue(
    participantScaling: ParticipantScaling<PlayTrackerScaleItem>,
    options?: ScalingTypeOptions,
  ): number {
    const isDoubles = participantScaling.scaleItems.every((item) => item.eventType === EventTypeEnum.DOUBLES);
    const values = participantScaling.scaleItems.map((scaleItem) => getRatingValue(scaleItem, options?.ptValueType));
    return values.reduce((total, rating) => total + rating, 0) / (isDoubles ? 1 : values.length);
  }

  static getIsAvailable(event: Event, levelConfig?: Partial<LevelConfiguration>) {
    const isPTEnabled = levelConfig?.playTracker !== false;
    const isTeam = event?.eventType === EventTypeEnum.TEAM;
    return isPTEnabled && !isTeam;
  }

  public static getDisplayName = (options?: ScalingOptions): string => {
    return options?.ptValueType ? options?.ptValueType.replace('PT', '').trim() : '';
  };

  public static getSubScalings = (): SubScalingType[] => ['PT Orange play', 'PT Green win', 'PT Green play'];
}

export default PlayTrackerScaling;
